import React, { useEffect, useState } from "react";
import { SwissDateInput } from "@/components/SwissDateInput";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { FinancialStatementsViewer } from "@/components/FinancialStatementsViewer";
import { VatStatementWidget } from "@/components/VatStatementWidget";
import { VatInstallmentsCard } from "@/components/vat/VatRatesAndInstallments";
import { BudgetTab } from "@/components/reports/BudgetTab";
import { CashFlowStatementCard } from "@/components/reports/CashFlowStatementCard";
import { ManagementReportCard } from "@/components/reports/ManagementReportCard";
import { FxRatesWidget } from "@/components/FxRatesWidget";
import { VendorHygieneWidget } from "@/components/VendorHygieneWidget";
import { AccountLedgerReport } from "@/components/AccountLedgerReport";
import { formatDate } from "@/lib/formatDate";
import { useCompany } from "@/context/CompanyContext";

function VatReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const companyId = localStorage.getItem("demoCompanyId");

  const loadVat = async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await apiClient.get(`/companies/${companyId}/vat-report`, { params });
      setData(res.data);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Laden der MWST-Abrechnung.");
    } finally {
      setLoading(false);
    }
  };

  const createSettlement = async () => {
    if (!companyId || !data) return;
    try {
      setError("");
      await apiClient.post(`/companies/${companyId}/vat-settlement`, {
        start_date: startDate || data.start_date,
        end_date: endDate || data.end_date,
      });
      await loadVat();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Erzeugen der MWST-Abschlussbuchung.");
    }
  };

  useEffect(() => {
    loadVat();
  }, []);

  return (
    <div className="space-y-3" data-testid="vat-report">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-600" htmlFor="vat-start-date">
            Von (inkl.)
          </label>
          <SwissDateInput
            className="w-full"
            inputClassName="w-full text-sm py-1.5"
            testid="vat-start-date"
            id="vat-start-date"
            value={startDate}
            onChange={setStartDate}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-600" htmlFor="vat-end-date">
            Bis (inkl.)
          </label>
          <SwissDateInput
            className="w-full"
            inputClassName="w-full text-sm py-1.5"
            testid="vat-end-date"
            id="vat-end-date"
            value={endDate}
            onChange={setEndDate}
          />
        </div>
        <div className="flex gap-2 md:justify-end">
          <Button
            type="button"
            onClick={loadVat}
            className="mt-4 rounded-full bg-slate-900 px-4 py-1.5 text-xs font-medium text-slate-50"
          >
            Aktualisieren
          </Button>
          {data?.net_vat_payable !== 0 && data && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={createSettlement}
              className="mt-4 rounded-full"
            >
              Abschlussbuchung erzeugen
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      {loading && (
        <div className="text-xs text-slate-400" data-testid="vat-report-loading">
          Lade MWST-Abrechnung ...
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="space-y-1">
            <div className="text-xs font-medium text-slate-500">Vorsteuer (Input Tax)</div>
            {data.input_vat ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <div>Konto: {data.input_vat.account_id}</div>
                <div>Debitsumme: {data.input_vat.debit.toFixed(2)}</div>
                <div>Creditsumme: {data.input_vat.credit.toFixed(2)}</div>
                <div>Saldo (Soll - Haben): {data.input_vat.balance.toFixed(2)}</div>
              </div>
            ) : (
              <div className="text-xs text-slate-400">Kein Vorsteuerkonto ausgewertet.</div>
            )}
          </div>

          <div className="space-y-1">
            <div className="text-xs font-medium text-slate-500">Umsatzsteuer (Output Tax)</div>
            {data.output_vat ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <div>Konto: {data.output_vat.account_id}</div>
                <div>Debitsumme: {data.output_vat.debit.toFixed(2)}</div>
                <div>Creditsumme: {data.output_vat.credit.toFixed(2)}</div>
                <div>Saldo (Soll - Haben): {data.output_vat.balance.toFixed(2)}</div>
              </div>
            ) : (
              <div className="text-xs text-slate-400">Kein Umsatzsteuerkonto ausgewertet.</div>
            )}
          </div>

          <div className="space-y-2">
            <div className="text-xs font-medium text-slate-500">Netto Zahllast / -Guthaben</div>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-xs text-slate-500">Zeitraum:</div>
              <div className="text-xs text-slate-700">
                {formatDate(data.start_date)} bis {formatDate(data.end_date)}
              </div>
              <div className="mt-2 text-sm font-semibold text-slate-900">
                {data.net_vat_payable.toFixed(2)} CHF
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Positiv = an ESTV zu bezahlen, negativ = Guthaben.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Reports() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "trial-balance";
  const [trialBalance, setTrialBalance] = useState(null);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [openInvoicesStart, setOpenInvoicesStart] = useState("");
  const [openInvoicesEnd, setOpenInvoicesEnd] = useState("");
  const [openInvoicesOverdueOnly, setOpenInvoicesOverdueOnly] = useState(false);
  const [openBills, setOpenBills] = useState([]);
  const [openBillsStart, setOpenBillsStart] = useState("");
  const [openBillsEnd, setOpenBillsEnd] = useState("");

  const [openBillsOverdueOnly, setOpenBillsOverdueOnly] = useState(false);
  const [loadingTB, setLoadingTB] = useState(false);
  const [loadingOI, setLoadingOI] = useState(false);
  const [loadingOB, setLoadingOB] = useState(false);

  const companyId = localStorage.getItem("demoCompanyId");
  const { company } = useCompany();
  const isPrivat = company?.company_type === "privat";

  const loadTrialBalance = async () => {
    if (!companyId) return;
    setLoadingTB(true);
    try {
      const res = await apiClient.get(`/companies/${companyId}/trial-balance`);
      setTrialBalance(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingTB(false);
    }
  };

  const loadOpenInvoices = async () => {
    if (!companyId) return;
    setLoadingOI(true);
    try {
      const params = {};
      if (openInvoicesStart) params.start_date = openInvoicesStart;
      if (openInvoicesEnd) params.end_date = openInvoicesEnd;
      if (openInvoicesOverdueOnly) params.overdue_only = true;
      const res = await apiClient.get(`/companies/${companyId}/reports/open-invoices`, { params });
      setOpenInvoices(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingOI(false);
    }
  };

  const loadOpenBills = async () => {
    if (!companyId) return;
    setLoadingOB(true);
    try {
      const params = {};
      if (openBillsStart) params.start_date = openBillsStart;
      if (openBillsEnd) params.end_date = openBillsEnd;
      if (openBillsOverdueOnly) params.overdue_only = true;
      const res = await apiClient.get(`/companies/${companyId}/reports/open-bills`, { params });
      setOpenBills(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingOB(false);
    }
  };

  useEffect(() => {
    loadTrialBalance();
    loadOpenInvoices();
    loadOpenBills();
  }, []);

  const exportOpenInvoicesCsv = () => {
    if (!openInvoices || openInvoices.length === 0) return;
    const header = [
      "Rechnung",
      "Datum",
      "Fällig",
      "Kunde",
      "Betrag",
      "Bezahlt",
      "Offen",
      "Status",
    ];
    const rows = openInvoices.map((inv) => [
      inv.invoice_number,
      inv.issue_date,
      inv.due_date,
      inv.customer_name || inv.customer_id,
      `${inv.total.toFixed(2)} ${inv.currency}`,
      `${inv.paid_amount.toFixed(2)} ${inv.currency}`,
      `${inv.outstanding_amount.toFixed(2)} ${inv.currency}`,
      inv.status,
    ]);
    const csvLines = [header.join(";"), ...rows.map((r) => r.join(";"))];
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "offene_rechnungen.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportOpenBillsCsv = () => {
    if (!openBills || openBills.length === 0) return;
    const header = [
      "Rechnung",
      "Datum",
      "Fällig",
      "Lieferant",
      "Betrag",
      "Bezahlt",
      "Offen",
      "Status",
    ];
    const rows = openBills.map((bill) => [
      bill.bill_number,
      bill.issue_date,
      bill.due_date,
      bill.supplier_name || bill.supplier_id,
      `${bill.total.toFixed(2)} ${bill.currency}`,
      `${bill.paid_amount.toFixed(2)} ${bill.currency}`,
      `${bill.outstanding_amount.toFixed(2)} ${bill.currency}`,
      bill.status,
    ]);
    const csvLines = [header.join(";"), ...rows.map((r) => r.join(";"))];
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "offene_eingangsrechnungen.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Auswertungen</h1>
        <p className="mt-1 text-sm text-slate-500">Überblick über Salden und offene Rechnungen.</p>
      </div>

      <Tabs defaultValue={initialTab} data-testid="reports-tabs">
        <TabsList data-testid="reports-tabs-list">
          <TabsTrigger value="trial-balance" data-testid="reports-tab-trial-balance">
            Summen/Saldenliste
          </TabsTrigger>
          <TabsTrigger value="account-ledger" data-testid="reports-tab-account-ledger">
            Kontoblätter
          </TabsTrigger>
          {!isPrivat && <TabsTrigger value="vat" data-testid="reports-tab-vat">
            MWST-Abrechnung
          </TabsTrigger>}
          {!isPrivat && <TabsTrigger value="open-invoices" data-testid="reports-tab-open-invoices">
            Offene Rechnungen
          </TabsTrigger>}
          <TabsTrigger value="open-bills" data-testid="reports-tab-open-bills">
            Offene Eingangsrechnungen
          </TabsTrigger>
          <TabsTrigger value="financial-statements" data-testid="reports-tab-financial-statements">
            Bilanz & Erfolgsrechnung
          </TabsTrigger>
          <TabsTrigger value="fx" data-testid="reports-tab-fx">
            Wechselkurse
          </TabsTrigger>
          <TabsTrigger value="vendor-hygiene" data-testid="reports-tab-vendor-hygiene">
            Stammdaten-Hygiene
          </TabsTrigger>
          <TabsTrigger value="budget" data-testid="reports-tab-budget">Budget</TabsTrigger>
        </TabsList>

        <TabsContent value="budget" className="mt-4">
          <BudgetTab companyId={localStorage.getItem("demoCompanyId")} />
        </TabsContent>

        {/* Trial Balance */}
        <TabsContent value="trial-balance" data-testid="reports-tabpanel-trial-balance">
          <Card
            className="p-4 border border-slate-200 bg-white shadow-sm mt-4"
            data-testid="trial-balance-card"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-medium text-slate-700">Summen- und Saldenliste</h2>
              {loadingTB && (
                <span
                  className="text-xs text-slate-400"
                  data-testid="trial-balance-loading-indicator"
                >
                  Lade ...
                </span>
              )}
            </div>

            <Table data-testid="trial-balance-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Konto</TableHead>
                  <TableHead>Soll</TableHead>
                  <TableHead>Haben</TableHead>
                  <TableHead>Saldo (Soll - Haben)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!trialBalance || trialBalance.lines.length === 0 ? (
                  <TableRow data-testid="trial-balance-empty-row">
                    <TableCell
                      colSpan={4}
                      className="text-center text-sm text-slate-400"
                      data-testid="trial-balance-empty-state"
                    >
                      Noch keine Buchungen vorhanden.
                    </TableCell>
                  </TableRow>
                ) : (
                  trialBalance.lines.map((line) => (
                    <TableRow
                      key={line.account_id}
                      data-testid={`trial-balance-row-${line.account_id}`}
                    >
                      <TableCell>{line.account_id}</TableCell>
                      <TableCell>{line.debit.toFixed(2)}</TableCell>
                      <TableCell>{line.credit.toFixed(2)}</TableCell>
                      <TableCell>{line.balance.toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Account Ledger (Kontoblätter) */}
        <TabsContent value="account-ledger" data-testid="reports-tabpanel-account-ledger">
          <AccountLedgerReport />
        </TabsContent>

        {/* VAT Report */}
        <TabsContent value="vat" data-testid="reports-tabpanel-vat">
          <div className="mt-4 space-y-4">
            <VatStatementWidget companyId={localStorage.getItem("demoCompanyId")} />
            <VatInstallmentsCard companyId={localStorage.getItem("demoCompanyId")} />
            <Card
              className="p-4 border border-slate-200 bg-white shadow-sm"
              data-testid="vat-report-card"
            >
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-medium text-slate-700">
                  MWST-Abrechnung (vereinfachte Ansicht)
                </h2>
              </div>
              <VatReport />
            </Card>
          </div>
        </TabsContent>

        {/* Open Invoices */}
        <TabsContent value="open-invoices" data-testid="reports-tabpanel-open-invoices">
          <Card
            className="p-4 border border-slate-200 bg-white shadow-sm mt-4"
            data-testid="open-invoices-card"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-medium text-slate-700">Offene Rechnungen</h2>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <div className="hidden md:flex items-center gap-2">
                  <span>Fällig von</span>
                  <SwissDateInput
                    value={openInvoicesStart}
                    onChange={setOpenInvoicesStart}
                    testid="open-invoices-start-date"
                  />
                  <span>bis</span>
                  <SwissDateInput
                    value={openInvoicesEnd}
                    onChange={setOpenInvoicesEnd}
                    testid="open-invoices-end-date"
                  />
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={openInvoicesOverdueOnly}
                      onChange={(e) => setOpenInvoicesOverdueOnly(e.target.checked)}
                      data-testid="open-invoices-overdue-only"
                    />
                    <span>nur überfällige</span>
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full px-3"
                    onClick={loadOpenInvoices}
                    data-testid="open-invoices-filter-apply"
                  >
                    Filter anwenden
                  </Button>
                </div>
                {loadingOI && (
                  <span
                    className="text-xs text-slate-400"
                    data-testid="open-invoices-loading-indicator"
                  >
                    Lade ...
                  </span>
                )}
              </div>
            </div>

            <Table data-testid="open-invoices-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Rechnung</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead>Fällig</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Betrag</TableHead>
                  <TableHead>Bezahlt</TableHead>
                  <TableHead>Offen</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {openInvoices.length === 0 ? (
                  <TableRow data-testid="open-invoices-empty-row">
                    <TableCell
                      colSpan={8}
                      className="text-center text-sm text-slate-400"
                      data-testid="open-invoices-empty-state"
                    >
                      Keine offenen Rechnungen.
                    </TableCell>
                  </TableRow>
                ) : (
                  openInvoices.map((inv) => (
                    <TableRow
                      key={inv.invoice_id}
                      data-testid={`open-invoice-row-${inv.invoice_id}`}
                    >
                      <TableCell>{inv.invoice_number}</TableCell>
                      <TableCell>{formatDate(inv.issue_date)}</TableCell>
                      <TableCell>{formatDate(inv.due_date)}</TableCell>
                      <TableCell>{inv.customer_name || inv.customer_id}</TableCell>
                      <TableCell>
                        {inv.total.toFixed(2)} {inv.currency}
                      </TableCell>
                      <TableCell>
                        {inv.paid_amount.toFixed(2)} {inv.currency}
                      </TableCell>
                      <TableCell>
                        {inv.outstanding_amount.toFixed(2)} {inv.currency}
                      </TableCell>
                      <TableCell>{inv.status}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <div className="mt-3 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full px-3 text-xs"
                onClick={exportOpenInvoicesCsv}
                data-testid="open-invoices-export-csv"
              >
                CSV exportieren
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* Open Bills */}
        <TabsContent value="open-bills" data-testid="reports-tabpanel-open-bills">
          <Card
            className="p-4 border border-slate-200 bg-white shadow-sm mt-4"
            data-testid="open-bills-card"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-medium text-slate-700">Offene Eingangsrechnungen</h2>
              <div className="flex items-center gap-3">
                <div className="hidden md:flex items-center gap-3 text-xs text-slate-500">
                  <span>Fällig von</span>
                  <SwissDateInput
                    value={openBillsStart}
                    onChange={setOpenBillsStart}
                    testid="open-bills-start-date"
                  />
                  <span>bis</span>
                  <SwissDateInput
                    value={openBillsEnd}
                    onChange={setOpenBillsEnd}
                    testid="open-bills-end-date"
                  />
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={openBillsOverdueOnly}
                      onChange={(e) => setOpenBillsOverdueOnly(e.target.checked)}
                      data-testid="open-bills-overdue-only"
                    />
                    <span>nur überfällige</span>
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full px-3"
                    onClick={loadOpenBills}
                    data-testid="open-bills-filter-apply"
                  >
                    Filter anwenden
                  </Button>
                </div>
                {loadingOB && (
                  <span
                    className="text-xs text-slate-400"
                    data-testid="open-bills-loading-indicator"
                  >
                    Lade ...
                  </span>
                )}
              </div>
            </div>

            <Table data-testid="open-bills-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Rechnung</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead>Fällig</TableHead>
                  <TableHead>Lieferant</TableHead>
                  <TableHead>Betrag</TableHead>
                  <TableHead>Bezahlt</TableHead>
                  <TableHead>Offen</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {openBills.length === 0 ? (
                  <TableRow data-testid="open-bills-empty-row">
                    <TableCell
                      colSpan={8}
                      className="text-center text-sm text-slate-400"
                      data-testid="open-bills-empty-state"
                    >
                      Keine offenen Eingangsrechnungen.
                    </TableCell>
                  </TableRow>
                ) : (
                  openBills.map((bill) => (
                    <TableRow
                      key={bill.bill_id}
                      data-testid={`open-bill-row-${bill.bill_id}`}
                    >
                      <TableCell>{bill.bill_number}</TableCell>
                      <TableCell>{formatDate(bill.issue_date)}</TableCell>
                      <TableCell>{formatDate(bill.due_date)}</TableCell>
                      <TableCell>{bill.supplier_name || bill.supplier_id}</TableCell>
                      <TableCell>
                        {bill.total.toFixed(2)} {bill.currency}
                      </TableCell>
                      <TableCell>
                        {bill.paid_amount.toFixed(2)} {bill.currency}
                      </TableCell>
                      <TableCell>
                        {bill.outstanding_amount.toFixed(2)} {bill.currency}
                      </TableCell>
                      <TableCell>{bill.status}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <div className="mt-3 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full px-3 text-xs"
                onClick={exportOpenBillsCsv}
                data-testid="open-bills-export-csv"
              >
                CSV exportieren
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* Financial Statements (Bilanz & Erfolgsrechnung) */}
        <TabsContent value="financial-statements" data-testid="reports-tabpanel-financial-statements">
          <div className="mt-4">
            <FinancialStatementsViewer />
            <CashFlowStatementCard companyId={localStorage.getItem("demoCompanyId")} />
            <ManagementReportCard companyId={localStorage.getItem("demoCompanyId")} />
          </div>
        </TabsContent>

        <TabsContent value="fx" data-testid="reports-tabpanel-fx">
          <div className="mt-4">
            <FxRatesWidget />
          </div>
        </TabsContent>

        <TabsContent value="vendor-hygiene" data-testid="reports-tabpanel-vendor-hygiene">
          <div className="mt-4">
            <VendorHygieneWidget />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
